import { User, ProductShared } from "pages/OrganizationAdmin/types";
import React, { useEffect, useMemo, useState, memo } from "react";
import { Tables, Typography, showToast } from "@deeptrue-frontend/ui";
import Search from "components/Fields/Search";
import api from "api";
import { useFormatMessage } from "components/IntlProvider";
import Product from "./Product";

const { Table, TableBody, TableHeader } = Tables;

interface Props {
  userData: User;
  isLoaded: boolean;
  organizationCodename: string;
  refreshUser: () => void;
  userId: number;
}

const Products = memo(
  ({
    userData,
    organizationCodename,
    isLoaded,
    refreshUser,
    userId,
  }: Props) => {
    const [searchedProducts, setSearchedProducts] = useState<ProductShared[]>(
      []
    );
    const [groupProducts, setGroupProducts] = useState<ProductShared[]>([]);
    const t = useFormatMessage();

    const getProducts = (searchValue?: string) => {
      return api.resources.organization.products
        .getProducts({
          organizationCodename: organizationCodename,
          params: {
            search: searchValue,
          },
        })
        .then(({ data }) => {
          setSearchedProducts(data.results);
        });
    };

    useEffect(() => {
      getProducts();
      // eslint-disable-next-line
    }, []);

    useEffect(() => {
      setGroupProducts(userData.shared_products);
    }, [userData.shared_products]);

    const addProductToUser = (id: number) => {
      const products = [
        ...userData.shared_products.map((product) => product.id),
        id,
      ];

      return api.resources.organization.users
        .updateUserInOrganization({
          organizationCodename: organizationCodename,
          userId: userId,
          data: { shared_products: products },
        })
        .then(() => {
          refreshUser();
          showToast({
            type: "success",
            title: t("organization.addedTo", "{x} added to {y}", {
              x: t("organization.product", "Product"),
              y: t("organization.user", "user"),
            }),
          });
        });
    };

    const removeProductFromUser = (id: number) => {
      const products = userData.shared_products
        .map((product) => product.id)
        .filter((productId) => productId !== id);

      return api.resources.organization.users
        .updateUserInOrganization({
          organizationCodename: organizationCodename,
          userId: userId,
          data: {
            shared_products: products,
          },
        })
        .then(() => {
          refreshUser();
          showToast({
            type: "success",
            title: t("organization.removedFrom", "{x} removed from {y}", {
              x: t("organization.product", "Product"),
              y: t("organization.user", "user"),
            }),
          });
        });
    };

    const options = searchedProducts.map((product: ProductShared) => ({
      id: product.id as number,
      label: `${product.name}  (${product.workbench.name || "-"})`,
    }));

    const filteredOptions = useMemo(() => {
      return options.filter((product: { id: number; label: string }) => {
        const itemIndex = groupProducts.findIndex((project) => {
          return project.id === product.id;
        });

        return itemIndex === -1;
      });
    }, [groupProducts, options]);

    return (
      <>
        <Typography fontWeight={600} mb="1rem">
          {t("organization.shareWithX", "Share {x} with {y}", {
            x: t("organization.product", "Product"),
            y: t("organization.user", "User"),
          })}
        </Typography>
        <Search
          options={filteredOptions.map(({ id, label }) => ({
            value: id,
            label,
          }))}
          styles={{
            maxWidth: "450px",
          }}
          name="user"
          onChange={(_name, project) => {
            if (project) {
              addProductToUser(project.value);
            }
          }}
          placeholder={t("organization.searchValue", "Search {name}", {
            name: t("organization.products", "Products"),
          })}
          asyncSeach={(searchedValue: string) => getProducts(searchedValue)}
        />
        <Typography fontWeight={600} mb="1rem" mt="2rem">
          {t("organization.sharedWith", "{x} shared with {y}", {
            x: t("organization.product", "Product"),
            y: t("organization.user", "user"),
          })}
          :
        </Typography>
        <Table>
          <TableHeader
            cells={[
              {
                text: t("organization.columns.name", "Name"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
              {
                text: t("organization.columns.workbench", "Workbench"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
              {
                text: t("organization.columns.resources", "Resources"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
            ]}
          />

          <TableBody
            isLoading={!isLoaded}
            withLoader
            isData={userData.shared_products.length > 0}
            noDataText={t(
              "noresults",
              "No results. We have nothing to show here."
            )}
          >
            {groupProducts.map((product: ProductShared) => {
              return (
                <Product
                  id={product.id}
                  name={product.name}
                  workbench={product.workbench.name}
                  key={product.id}
                  removeProduct={removeProductFromUser}
                />
              );
            })}
          </TableBody>
        </Table>
      </>
    );
  }
);

export default Products;
